import React, { createContext, useContext, useState, useCallback } from 'react';

interface WizardStep {
  id: string;
  targetSelector: string;
  message: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

interface WizardContextType {
  currentStep: WizardStep | null;
  stepIndex: number;
  isWizardActive: boolean;
  startWizard: (steps: WizardStep[]) => void;
  nextStep: () => void;
  previousStep: () => void;
  closeWizard: () => void;
  skipWizard: () => void;
  setSteps: (steps: WizardStep[]) => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [steps, setStepsState] = useState<WizardStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [isWizardActive, setIsWizardActive] = useState(false);

  const startWizard = useCallback((newSteps: WizardStep[]) => {
    setStepsState(newSteps);
    setStepIndex(0);
    setIsWizardActive(true);
    localStorage.setItem('wizard-active', 'true');
  }, []);

  const nextStep = useCallback(() => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      closeWizard();
    }
  }, [stepIndex, steps.length]);

  const previousStep = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  }, [stepIndex]);

  const closeWizard = useCallback(() => {
    setIsWizardActive(false);
    setStepIndex(0);
    localStorage.setItem('wizard-active', 'false');
  }, []);

  const skipWizard = useCallback(() => {
    setIsWizardActive(false);
    setStepIndex(0);
    localStorage.setItem('wizard-completed', 'true');
  }, []);

  const setSteps = useCallback((newSteps: WizardStep[]) => {
    setStepsState(newSteps);
  }, []);

  const currentStep = isWizardActive && steps.length > 0 ? steps[stepIndex] : null;

  return (
    <WizardContext.Provider
      value={{
        currentStep,
        stepIndex,
        isWizardActive,
        startWizard,
        nextStep,
        previousStep,
        closeWizard,
        skipWizard,
        setSteps,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
};
